/**
 * The `vueComponents` object maps element IDs to their corresponding Vue components,
 * allowing for dynamic, lazy-loaded component mounting.
 *
 * Each key in the `vueComponents` object corresponds to the ID of an HTML element in your Blade templates.
 * When an element with a matching ID is found in the DOM, the corresponding Vue component is dynamically
 * imported using `import()` and mounted onto the element.
 *
 * The `hasRouter` property indicates whether the component should use Vue Router.
 * The `vuexModule` property (optional) defines a Vuex module that will be dynamically registered
 * when the component is mounted, allowing it to manage its own state within Vuex.
 *
 * Example:
 *
 * In Blade:
 * <pricing-page id="pricing-page"></pricing-page>
 *
 * In JavaScript:
 * export const vueComponents = {
 *     'pricing-page': {
 *         component: () => import('./pages/Pricing/PricingPage.vue'),
 *         hasRouter: true,  // Indicates that this component uses Vue Router
*          props: ["exampleData"],
 *         vuexModule: {     // Optional Vuex module for this component
 *             namespaced: true,
 *             state() {
 *                 return { someState: null };
 *             },
 *             mutations: {
 *                 setSomeState(state, value) {
 *                     state.someState = value;
 *                 }
 *             },
 *             actions: {
 *                 updateSomeState({ commit }, value) {
 *                     commit('setSomeState', value);
 *                 }
 *             },
 *             getters: {
 *                 getSomeState(state) {
 *                     return state.someState;
 *                 }
 *             }
 *         }
 *     },
 * };
 *
 * This setup will dynamically load and mount `PricingPage.vue` onto the element with ID `pricing-page`,
 * and if `hasRouter` is set to true, Vue Router will be applied.
 * If the `vuexModule` is defined, it will be registered dynamically in Vuex to manage component-specific state.
 */
export const vueComponents = {
    'pricing-page': {
        component: () => import(/* webpackChunkName: "pricing-page" */ './components/PricingPage/PricingPage.vue'),
        hasRouter: true,
        /*
        vuexModule: {
            // Optional Vuex module to handle specific component state.
            // Example can be found in the _global.js file.
        },
        */
    },
    'payments-widget': {
        component: () => import(/* webpackChunkName: "payments-widget" */ './components/PaymentsWidget.vue'),
        hasRouter: false,
        props: ["creditPlans", "creditBalances"],
    },
    'billing-address-vue': {
        component: () => import(/* webpackChunkName: "billing-address" */ './components/BillingComponentWrapper.vue'),
        hasRouter: false,
    },
    'manage-emails-component': {
        component: () => import(/* webpackChunkName: "manage-emails" */ './components/ManageEmailsComponent.vue'),
        hasRouter: false,
        props: ["emails"],
    },
    'vin-scanner-component': {
        component: () => import('./components/vin-scanner/VinScannerComponent.vue'),
        hasRouter: false,
    },
    'carvago-ads-container': {
        component: () => import('./components/CarvagoAds.vue'),
        hasRouter: false,
        props: ["vin"],
    },
    'stolen-vehicle-report-form': {
        component: () => import(/* webpackChunkName: "stolen-vehicle-report-form" */ './components/StolenVehicleReportForm.vue'),
        hasRouter: false,
    },
    'roi-calculator': {
        component: () => import(/* webpackChunkName: "roi-calculator" */ './components/RoiCalculator.vue'),
        hasRouter: false,
        props: ["currencyCode", "currencies", "exchangeRates", "pricingUrl", "requestCallUrl", "demoUrl"],
    },
};
